import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Alert, CircularProgress, IconButton, Tooltip,
  Paper, Grid, Divider, Button,
} from '@mui/material';
import {
  VerifiedUser, Warning, Error as ErrorIcon, Refresh, SmartToy,
  CheckCircle, Schedule, GppBad, GppMaybe,
} from '@mui/icons-material';
import { addEventListener, removeEventListener, requestLicenseStatus } from '../../utils/socketClient';
import { useWebSocket } from '../../hooks/useWebSocket';
import apiClient from '../../utils/apiClient';

interface LicenseInfo {
  customer: string;
  serial: string;
  type: string;
  days_remaining: number;
  features: string;
  raw?: string;
}

interface LicenseAlert {
  level: 'critical' | 'warning' | 'info';
  message: string;
}

interface LicenseEntry {
  status: string;
  message: string;
  info: LicenseInfo;
  alerts: LicenseAlert[];
  timestamp: number | string;   // v6.3.25: ISO string when seeded from DB
  last_check?: string;          // v6.3.25: persisted per-robot check time
}

const LicenseManagement: React.FC = () => {
  const { robotsOnline } = useWebSocket();
  const [licenses, setLicenses] = useState<Record<string, LicenseEntry>>({});
  const [robots, setRobots] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [allAlerts, setAllAlerts] = useState<{ serial: string; alert: LicenseAlert; timestamp: number }[]>([]);

  const fetchRobots = useCallback(async () => {
    try {
      const response = await apiClient.get('/admin/robots');
      setRobots(response.data);
      // v6.3.23: seed from persisted robot_metadata.license so offline
      // robots still show their last known license + when it was last
      // checked (server restart no longer wipes the page).
      setLicenses(prev => {
        const merged: any = { ...prev };
        for (const r of response.data || []) {
          const lic = r.robot_metadata?.license;
          if (lic && !merged[r.serial_number]) {
            merged[r.serial_number] = {
              status: lic.status, message: lic.message,
              info: lic.info || {}, alerts: [],
              timestamp: lic.last_check, last_check: lic.last_check,
            };
          } else if (lic && merged[r.serial_number]) {
            merged[r.serial_number].last_check = lic.last_check;
          }
        }
        return merged;
      });
    } catch {}
  }, []);

  useEffect(() => {
    fetchRobots();
    requestLicenseStatus();
    
    const handleLicenseStatus = (data: any) => {
      const serial = data.robot_serial;
      if (!serial) return;
      
      setLicenses(prev => ({
        ...prev,
        [serial]: {
          status: data.status,
          message: data.message,
          info: data.info || {},
          alerts: data.alerts || [],
          timestamp: data.timestamp,
        }
      }));
      
      // Collect alerts
      if (data.alerts && data.alerts.length > 0) {
        setAllAlerts(prev => {
          const newAlerts = data.alerts.map((a: LicenseAlert) => ({
            serial, alert: a, timestamp: data.timestamp
          }));
          // Keep last 50 alerts, deduplicate by serial+message
          const existing = prev.filter(a => a.serial !== serial || a.alert.message !== newAlerts[0]?.alert?.message);
          return [...newAlerts, ...existing].slice(0, 50);
        });
      }
    };
    
    const handleLicenseCache = (data: any) => {
      if (data.licenses) {
        setLicenses(data.licenses);
      }
      setLoading(false);
    };
    
    addEventListener('license_status', handleLicenseStatus);
    addEventListener('license_cache', handleLicenseCache);
    
    // Stop loading after 3s even if no data
    const timer = setTimeout(() => setLoading(false), 3000);
    
    // Refresh every 5 minutes
    const interval = setInterval(() => requestLicenseStatus(), 300000);
    
    return () => {
      removeEventListener('license_status', handleLicenseStatus);
      removeEventListener('license_cache', handleLicenseCache);
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [fetchRobots]);

  const handleRefresh = () => {
    setLoading(true);
    requestLicenseStatus();
    fetchRobots();
    setTimeout(() => setLoading(false), 3000);
  };

  const getStatusChip = (entry: LicenseEntry | undefined, isOnline: boolean) => {
    if (!isOnline) return <Chip size="small" label="Offline" sx={{ bgcolor: 'rgba(158,158,158,0.2)', color: '#9e9e9e' }} />;
    if (!entry) return <Chip size="small" label="Pending" sx={{ bgcolor: 'rgba(158,158,158,0.2)', color: '#9e9e9e' }} icon={<Schedule sx={{ fontSize: 14 }} />} />;
    if (entry.status === 'error') return <Chip size="small" label="Error" color="error" icon={<ErrorIcon sx={{ fontSize: 14 }} />} />;
    
    const days = entry.info?.days_remaining || 0;
    const hasAlerts = entry.alerts && entry.alerts.some(a => a.level === 'critical');
    
    if (hasAlerts || days <= 0) return <Chip size="small" label="Critical" color="error" icon={<GppBad sx={{ fontSize: 14 }} />} />;
    if (days <= 30) return <Chip size="small" label="Expiring" color="warning" icon={<GppMaybe sx={{ fontSize: 14 }} />} />;
    return <Chip size="small" label="Valid" color="success" icon={<CheckCircle sx={{ fontSize: 14 }} />} />;
  };

  const criticalAlerts = allAlerts.filter(a => a.alert.level === 'critical');
  const warningAlerts = allAlerts.filter(a => a.alert.level === 'warning');

  const totalRobots = robots.length;
  const licensedRobots = Object.keys(licenses).length;
  const validCount = Object.values(licenses).filter(l => l.status === 'ok' && l.info?.days_remaining > 30).length;
  const expiringCount = Object.values(licenses).filter(l => l.info?.days_remaining > 0 && l.info?.days_remaining <= 30).length;
  const criticalCount = Object.values(licenses).filter(l => l.status === 'error' || (l.info?.days_remaining || 0) <= 0 || l.alerts?.some(a => a.level === 'critical')).length;

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VerifiedUser color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>License Management</Typography>
            <Typography variant="body2" color="text.secondary">Monitor robot licenses, expiry, and tampering alerts</Typography>
          </Box>
        </Box>
        <Button startIcon={loading ? <CircularProgress size={16} /> : <Refresh />} onClick={handleRefresh} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Robots', value: totalRobots, color: '#2196f3', icon: <SmartToy /> },
          { label: 'Valid Licenses', value: validCount, color: '#69f0ae', icon: <CheckCircle /> },
          { label: 'Expiring Soon', value: expiringCount, color: '#ff9800', icon: <GppMaybe /> },
          { label: 'Critical Issues', value: criticalCount, color: '#ff5252', icon: <GppBad /> },
        ].map((stat) => (
          <Grid item xs={6} md={3} key={stat.label}>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ color: stat.color, mb: 0.5 }}>{stat.icon}</Box>
                <Typography variant="h4" fontWeight={700} sx={{ color: stat.color }}>{stat.value}</Typography>
                <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Active Alerts */}
      {criticalAlerts.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<GppBad />}>
          <Typography variant="subtitle2">Critical License Alerts</Typography>
          {criticalAlerts.map((a, i) => (
            <Typography key={i} variant="body2">
              <strong>{a.serial}</strong>: {a.alert.message}
            </Typography>
          ))}
        </Alert>
      )}
      {warningAlerts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          <Typography variant="subtitle2">License Warnings</Typography>
          {warningAlerts.map((a, i) => (
            <Typography key={i} variant="body2">
              <strong>{a.serial}</strong>: {a.alert.message}
            </Typography>
          ))}
        </Alert>
      )}

      {/* License Table */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Robot</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>License Type</TableCell>
                  <TableCell>Days Remaining</TableCell>
                  <TableCell>Features</TableCell>
                  <TableCell>Last Check</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {robots.map((robot) => {
                  const entry = licenses[robot.serial_number];
                  const isOnline = robotsOnline.includes(robot.serial_number);
                  const info = entry?.info || {} as LicenseInfo;
                  const days = info.days_remaining || 0;
                  
                  return (
                    <TableRow key={robot.id} sx={{
                      bgcolor: entry?.alerts?.some(a => a.level === 'critical') ? 'rgba(255,82,82,0.05)' : 'transparent'
                    }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <SmartToy sx={{ fontSize: 18, color: isOnline ? 'success.main' : 'text.disabled' }} />
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{robot.serial_number}</Typography>
                            <Typography variant="caption" color="text.secondary">{robot.friendly_name || robot.model_number}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{getStatusChip(entry, isOnline)}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{info.customer || '--'}</Typography>
                      </TableCell>
                      <TableCell>
                        {info.type ? (
                          <Chip size="small" label={info.type} variant="outlined" />
                        ) : '--'}
                      </TableCell>
                      <TableCell>
                        {entry && isOnline ? (
                          <Typography variant="body2" sx={{
                            color: days <= 0 ? 'error.main' : days <= 30 ? 'warning.main' : 'success.main',
                            fontWeight: days <= 30 ? 600 : 400,
                          }}>
                            {days <= 0 ? 'EXPIRED' : `${days} days`}
                          </Typography>
                        ) : '--'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {info.features || '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {entry?.timestamp ? new Date(entry.timestamp).toLocaleString() : '--'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {robots.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography color="text.secondary">No robots registered</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
        License data refreshes automatically every hour from each robot. Click Refresh to request immediate update.
      </Typography>
    </Box>
  );
};

export default LicenseManagement;
